import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import type { EnderecoApi, SalvarEnderecoRequest } from "../types";
import { toast } from "sonner";

export const usePersonAddresses = (personId: string | undefined) => {
  const queryClient = useQueryClient();
  const numId = personId ? Number(personId) : undefined;

  const query = useQuery({
    queryKey: ["person-addresses", numId],
    queryFn: async () => {
      const { data } = await apiClient.get<EnderecoApi[]>(
        `/api/clientes/${numId}/enderecos`
      );
      return data;
    },
    enabled: !!numId,
  });

  const createMutation = useMutation({
    mutationFn: async (body: SalvarEnderecoRequest) => {
      if (!numId) throw new Error("Person ID is required");
      const { data } = await apiClient.post<EnderecoApi>(
        `/api/clientes/${numId}/enderecos`,
        body
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["person-addresses", numId] });
      toast.success("Endereço cadastrado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao cadastrar endereço: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SalvarEnderecoRequest }) => {
      const { data: updated } = await apiClient.put<EnderecoApi>(
        `/api/clientes/${numId}/enderecos/${id}`,
        data
      );
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["person-addresses", numId] });
      toast.success("Endereço atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar endereço: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/clientes/${numId}/enderecos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["person-addresses", numId] });
      toast.success("Endereço excluído com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir endereço: ${error.message}`);
    },
  });

  return {
    addresses:     query.data ?? [],
    isLoading:     query.isLoading,
    error:         query.error,
    createAddress: createMutation.mutate,
    updateAddress: updateMutation.mutate,
    deleteAddress: deleteMutation.mutate,
    isCreating:    createMutation.isPending,
    isUpdating:    updateMutation.isPending,
    isDeleting:    deleteMutation.isPending,
  };
};
